import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import BottomNav from './BottomNav';
import AIAssistant from '@/components/ai/AIAssistant';
import { TourProvider } from '@/components/common/OnboardingTour';
import { MobileFilterDrawer } from '@/components/common/MobileFilterDrawer';
import { SlidersHorizontal } from 'lucide-react';
import { useFiltersStore, getActiveFilterCount } from '@/store/filtersStore';

// Páginas que tienen GlobalFilterBar y necesitan el botón flotante de filtros móvil
const FILTERABLE_PATHS = ['/ops', '/liderazgo', '/near-misses', '/siniestralidad', '/dashboard'];

export default function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const location = useLocation();
  const activeCount = useFiltersStore(getActiveFilterCount);
  const showFilterBtn = FILTERABLE_PATHS.some(p => location.pathname.startsWith(p));

  return (
    <TourProvider>
    <div
      className="flex h-screen overflow-hidden"
      style={{ background: 'var(--vz-bg-primary)' }}
      data-testid="app-shell"
    >
      {/* Sidebar - desktop */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-y-auto overflow-x-hidden pb-16 lg:pb-0" style={{ background: 'var(--vz-bg-primary)' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Bottom nav - mobile */}
      <BottomNav />

      {/* Floating filter button — mobile only, shown on filterable pages */}
      {showFilterBtn && (
        <button
          onClick={() => setFilterDrawerOpen(true)}
          data-testid="mobile-filter-fab"
          className="lg:hidden fixed bottom-20 right-4 z-30 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95"
          style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}
        >
          <SlidersHorizontal size={18} style={{ color: 'var(--vz-text-secondary)' }} />
          {activeCount > 0 && (
            <span
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center"
              style={{ background: 'var(--vz-blue)', color: '#fff' }}
            >
              {activeCount}
            </span>
          )}
        </button>
      )}

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} />

      {/* AI Assistant floating button */}
      <AIAssistant />
    </div>
    </TourProvider>
  );
}
